import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PageShell from "@/components/brand/PageShell";
import { api } from "@/lib/api";

function fmtDollars(cents) {
    return ((cents || 0) / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function Badge({ status }) {
    const map = {
        pending:      { bg: "#FFFAEB", fg: "#B54708" },
        paid:         { bg: "#ECFDF3", fg: "#067647" },
        clawed_back:  { bg: "#FEF3F2", fg: "#B42318" },
    };
    const s = map[status] || { bg: "var(--st-silver-100)", fg: "var(--st-ink-muted)" };
    return (
        <span className="px-2 py-0.5 rounded-full text-[10px] tracking-caps font-bold"
              style={{ background: s.bg, color: s.fg }}>
            {status}
        </span>
    );
}

export function AffiliateCommissions() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.affiliateCommissions({ limit: 200 })
            .then((r) => { setRows(r.commissions); setTotal(r.total); })
            .finally(() => setLoading(false));
    }, []);

    return (
        <PageShell>
            <section className="max-w-4xl mx-auto px-6 py-10 space-y-6" data-testid="affiliate-commissions-page">
                <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>
                    <Link to="/affiliate" className="hover:underline inline-flex items-center gap-1">
                        <ArrowLeft size={12} /> Dashboard
                    </Link>
                </div>
                <div>
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Commissions</div>
                    <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>Full commission history</h1>
                </div>
                <div className="st-surface overflow-hidden" data-testid="commissions-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr style={{ background: "var(--st-silver-50)", color: "var(--st-ink-muted)" }}>
                                <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Earned</th>
                                <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Student</th>
                                <th className="text-right px-4 py-3 tracking-caps text-[10px] font-semibold">Gross</th>
                                <th className="text-right px-4 py-3 tracking-caps text-[10px] font-semibold">Rate</th>
                                <th className="text-right px-4 py-3 tracking-caps text-[10px] font-semibold">Commission</th>
                                <th className="text-center px-4 py-3 tracking-caps text-[10px] font-semibold">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={6} className="text-center py-8" style={{ color: "var(--st-silver-500)" }}>Loading…</td></tr>}
                            {!loading && rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 italic" style={{ color: "var(--st-silver-500)" }}>No commissions yet.</td></tr>}
                            {rows.map((c) => (
                                <tr key={c.id} style={{ borderTop: "1px solid var(--st-line)" }} data-testid={`commission-row-${c.id}`}>
                                    <td className="px-4 py-3 text-xs">{new Date(c.earned_at).toLocaleDateString()}</td>
                                    <td className="px-4 py-3">{c.student_email_masked}</td>
                                    <td className="px-4 py-3 text-right">{fmtDollars(c.gross_amount_cents)}</td>
                                    <td className="px-4 py-3 text-right">{Math.round(c.commission_rate * 100)}%</td>
                                    <td className="px-4 py-3 text-right font-semibold" style={{ color: "var(--st-navy)" }}>{fmtDollars(c.commission_amount_cents)}</td>
                                    <td className="px-4 py-3 text-center"><Badge status={c.status} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="text-xs" style={{ color: "var(--st-silver-500)" }}>{rows.length} of {total} commissions.</p>
            </section>
        </PageShell>
    );
}

export function AffiliatePayouts() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.affiliatePayouts()
            .then((r) => setRows(r.payouts))
            .finally(() => setLoading(false));
    }, []);

    return (
        <PageShell>
            <section className="max-w-4xl mx-auto px-6 py-10 space-y-6" data-testid="affiliate-payouts-page">
                <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>
                    <Link to="/affiliate" className="hover:underline inline-flex items-center gap-1">
                        <ArrowLeft size={12} /> Dashboard
                    </Link>
                </div>
                <div>
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Payouts</div>
                    <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>Payout history</h1>
                </div>
                <div className="st-surface overflow-hidden" data-testid="payouts-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr style={{ background: "var(--st-silver-50)", color: "var(--st-ink-muted)" }}>
                                <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Paid</th>
                                <th className="text-right px-4 py-3 tracking-caps text-[10px] font-semibold">Total</th>
                                <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Method</th>
                                <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Reference</th>
                                <th className="text-right px-4 py-3 tracking-caps text-[10px] font-semibold">Commissions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={5} className="text-center py-8" style={{ color: "var(--st-silver-500)" }}>Loading…</td></tr>}
                            {!loading && rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 italic" style={{ color: "var(--st-silver-500)" }}>No payouts yet.</td></tr>}
                            {rows.map((p) => (
                                <tr key={p.id} style={{ borderTop: "1px solid var(--st-line)" }} data-testid={`payout-row-${p.id}`}>
                                    <td className="px-4 py-3 text-xs">{new Date(p.paid_at).toLocaleDateString()}</td>
                                    <td className="px-4 py-3 text-right font-semibold" style={{ color: "var(--st-navy)" }}>{fmtDollars(p.total_amount_cents)}</td>
                                    <td className="px-4 py-3">{p.method}</td>
                                    <td className="px-4 py-3 text-xs" style={{ color: "var(--st-ink-muted)" }}>{p.reference || "—"}</td>
                                    <td className="px-4 py-3 text-right">{p.commission_ids.length}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </PageShell>
    );
}
